import { useState, useEffect } from 'react';
import { Check, X, Info } from 'lucide-react';
import { Button } from '../ui/button';
import axios from 'axios';
import { toast } from 'sonner';
import { BACKEND_CONFIG } from '../../utils/productionConfig';

const API_URL = BACKEND_CONFIG.apiUrl;

export default function AdminGSTSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    isEnabled: false,
    gstPercentage: 18,
    gstNumber: '',
    companyName: 'Atootbandan',
    companyAddress: ''
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_URL}/gst-settings`);
      if (data.success) {
        setSettings(data.data);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch GST settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const { data } = await axios.put(
        `${API_URL}/gst-settings`,
        settings,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (data.success) {
        toast.success('GST settings updated successfully');
        setSettings(data.data);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update GST settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl mb-2">GST & Tax Settings</h2>
        <p className="text-sm text-gray-600">
          Configure GST/Tax settings for plan purchases
        </p>
      </div>

      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="text-sm text-blue-900 mb-1">💡 How GST Works</h4>
            <p className="text-xs text-blue-700 leading-relaxed">
              When GST is <strong>enabled</strong>, the payment modal will show: <strong>Base Price + GST Amount = Total Amount</strong>. 
              When <strong>disabled</strong>, only the plan price is shown without any tax breakdown.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-6">
        {/* Enable GST Toggle */}
        <div className="flex items-center justify-between pb-4 border-b">
          <div>
            <h3 className="text-lg mb-1">Enable GST</h3>
            <p className="text-sm text-gray-600">
              Show GST breakdown in payment modals
            </p>
          </div>
          <button
            onClick={() => setSettings({ ...settings, isEnabled: !settings.isEnabled })}
            className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors ${
              settings.isEnabled ? 'bg-green-500' : 'bg-gray-300'
            }`}
          >
            <span
              className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                settings.isEnabled ? 'translate-x-7' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {/* GST Percentage */}
        <div>
          <label className="block text-sm mb-2">
            GST Percentage (%) *
          </label>
          <input
            type="number"
            min="0"
            max="100"
            step="0.01"
            value={settings.gstPercentage}
            onChange={(e) => setSettings({ ...settings, gstPercentage: parseFloat(e.target.value) || 0 })}
            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
            placeholder="18"
          />
          <p className="text-xs text-gray-500 mt-1">
            Common values: 5%, 12%, 18%, 28%
          </p>
        </div>

        {/* GST Number */}
        <div>
          <label className="block text-sm mb-2">
            GST Number (Optional)
          </label>
          <input
            type="text"
            value={settings.gstNumber}
            onChange={(e) => setSettings({ ...settings, gstNumber: e.target.value })}
            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
            placeholder="22AAAAA0000A1Z5"
          />
          <p className="text-xs text-gray-500 mt-1">
            15-character alphanumeric GST identification number
          </p>
        </div>

        {/* Company Name */}
        <div>
          <label className="block text-sm mb-2">
            Company Name
          </label>
          <input
            type="text"
            value={settings.companyName}
            onChange={(e) => setSettings({ ...settings, companyName: e.target.value })}
            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
            placeholder="Atootbandan"
          />
          <p className="text-xs text-gray-500 mt-1">
            Will be displayed on payment receipts/invoices
          </p>
        </div>

        {/* Company Address */}
        <div>
          <label className="block text-sm mb-2">
            Company Address (Optional)
          </label>
          <textarea
            value={settings.companyAddress}
            onChange={(e) => setSettings({ ...settings, companyAddress: e.target.value })}
            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
            rows={3}
            placeholder="123 Main Street, City, State - 123456"
          />
          <p className="text-xs text-gray-500 mt-1">
            Full registered address (for invoices)
          </p>
        </div>

        {/* Preview Section */}
        {settings.isEnabled && (
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <h4 className="text-sm mb-3">Preview: Payment Breakdown</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Plan Price</span>
                <span>₹1,000</span>
              </div>
              <div className="flex justify-between text-green-600">
                <span>GST ({settings.gstPercentage}%)</span>
                <span>₹{((1000 * settings.gstPercentage) / 100).toFixed(2)}</span>
              </div>
              <div className="border-t pt-2 flex justify-between">
                <span>Total Amount</span>
                <span>₹{(1000 + (1000 * settings.gstPercentage) / 100).toFixed(2)}</span>
              </div>
            </div>
          </div>
        )}

        {/* Save Button */}
        <div className="flex gap-3 pt-4">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 bg-gradient-to-r from-rose-500 to-pink-600 text-white"
          >
            <Check className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
          <Button
            variant="outline"
            onClick={fetchSettings}
            disabled={saving}
            className="flex-1"
          >
            <X className="w-4 h-4 mr-2" />
            Reset
          </Button>
        </div>
      </div>
    </div>
  );
}
